import { useMutation } from '@apollo/client';
import { launchExperimentMutation } from 'api/mutations/experiments';
import { addNegativeToast, addPositiveToast, errorIdentifier } from 'modules/toastContainer';
import { useDispatch } from 'react-redux';
import { useExperimentsTranslations } from '../useTranslations';

type CallbackFn = () => void;

type LaunchExperiment = {
  id: number;
  name?: string;
  showSuccessToaster?: boolean;
  /** Do the same action whether the operation is a success or not */
  onComplete?: CallbackFn;
  onSuccess?: CallbackFn;
  onError?: CallbackFn;
};

const source = 'useLaunchExperiment';

export const useLaunchExperiment = ({
  id,
  name,
  showSuccessToaster = false,
  onComplete,
  onSuccess,
  onError,
}: LaunchExperiment) => {
  const { t } = useExperimentsTranslations();
  const dispatch = useDispatch();
  const onSuccessCallback = onSuccess || onComplete;
  const onErrorCallback = onError || onComplete;
  const errorMessage = `Failed to launch experiment id ${id}`;
  const errorToasterMessage = t('errorMessage.launchExperiment');
  const successToasterMessage = t('successMessage.launchExperiment', { experiment: name });

  const errorHandler = (error: unknown) => {
    if (onErrorCallback) onErrorCallback();
    dispatch(
      addNegativeToast(
        errorToasterMessage,
        errorIdentifier({
          errorMessage,
          source,
          error,
        }),
      ),
    );
  };

  const [launchExperiment] = useMutation(launchExperimentMutation, {
    onError: errorHandler,
  });

  return async () => {
    const { data } = await launchExperiment({
      variables: { id },
    });

    if (data?.startExperiment?.id) {
      if (onSuccessCallback) onSuccessCallback();
      if (showSuccessToaster) {
        dispatch(addPositiveToast(successToasterMessage));
      }
    }

    if (data?.startExperiment?.message) {
      errorHandler(data.startExperiment.message);
    }
  };
};
